import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Send, Linkedin, Twitter, Github, Instagram } from "lucide-react";
import { Reveal, SectionHead } from "../components/Reveal";
import { SERVICES, COMPANY } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-xl border border-input bg-card px-4 py-3 text-sm outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (process.env.REACT_APP_BACKEND_URL) {
        await axios.post(`${API}/contact`, form);
      } else {
        await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ "form-name": "contact", ...form }).toString(),
        });
      }
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="glass-card rounded-2xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cf-name" className="block text-xs font-medium mb-1.5 text-muted-foreground">Full Name *</label>
          <input id="cf-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name-input" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-email" className="block text-xs font-medium mb-1.5 text-muted-foreground">Email *</label>
          <input id="cf-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-email-input" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-phone" className="block text-xs font-medium mb-1.5 text-muted-foreground">Phone</label>
          <input id="cf-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" data-testid="contact-phone-input" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-company" className="block text-xs font-medium mb-1.5 text-muted-foreground">Company</label>
          <input id="cf-company" value={form.company} onChange={set("company")} placeholder="Company name" data-testid="contact-company-input" className={inputCls} />
        </div>
      </div>
      <div>
        <label htmlFor="cf-service" className="block text-xs font-medium mb-1.5 text-muted-foreground">Service Required</label>
        <select id="cf-service" value={form.service} onChange={set("service")} data-testid="contact-service-select" className={inputCls}>
          <option value="">Select a service…</option>
          {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
          <option value="Other">Other / Not sure yet</option>
        </select>
      </div>
      <div>
        <label htmlFor="cf-message" className="block text-xs font-medium mb-1.5 text-muted-foreground">Message *</label>
        <textarea id="cf-message" required minLength={5} rows={5} value={form.message} onChange={set("message")}
          placeholder="Tell us about your project…" data-testid="contact-message-input" className={inputCls} />
      </div>
      <button type="submit" disabled={busy} data-testid="contact-form-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 py-3.5 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(99,102,241,0.4)] hover:opacity-95 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50">
        {busy ? "Sending…" : <>Send Message <Send size={15} /></>}
      </button>
    </form>
  );
};

export default function Contact() {
  const socials = [
    { icon: Linkedin, label: "LinkedIn", id: "linkedin" },
    { icon: Twitter, label: "Twitter", id: "twitter" },
    { icon: Github, label: "GitHub", id: "github" },
    { icon: Instagram, label: "Instagram", id: "instagram" },
  ];

  return (
    <main className="pt-28">
      <section data-testid="contact-section" className="section-pad !pt-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-indigo-500/15 blur-[110px] pointer-events-none" aria-hidden="true" />
        <div className="absolute top-1/2 -left-24 w-[320px] h-[320px] rounded-full bg-cyan-400/15 blur-[110px] pointer-events-none" aria-hidden="true" />
        <div className="container-x relative">
          <SectionHead badge="Contact" title="Let's Build Something Great"
            sub="Tell us about your project — expect a reply from a real engineer within 24 hours." />
          <div className="grid sm:grid-cols-3 gap-5 mb-12">
            {[
              "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80",
              "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=800&q=80",
              "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=800&q=80",
            ].map((src, i) => (
              <Reveal key={src} delay={i * 0.08}>
                <img src={src} alt="VAELIX TECHNOLOGY team and office" data-testid={`contact-banner-img-${i}`}
                  className="rounded-2xl border border-border/60 shadow-lg w-full h-40 sm:h-48 object-cover hover:scale-[1.03] transition-transform duration-300" />
              </Reveal>
            ))}
          </div>
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
            <Reveal><ContactForm /></Reveal>
            <Reveal delay={0.12} className="space-y-5">
              <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
                alt="VAELIX TECHNOLOGY office in Pune" data-testid="contact-office-image"
                className="rounded-2xl border border-border/60 shadow-lg w-full h-44 object-cover" />
              <div className="glass-card rounded-2xl p-6 space-y-5" data-testid="office-info-card">
                <h3 className="font-display font-semibold text-lg">Office Information</h3>
                <div className="flex items-start gap-3.5">
                  <span className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0"><Mail size={17} /></span>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email</p>
                    <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="text-sm font-semibold hover:text-primary transition-colors">{COMPANY.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3.5">
                  <span className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0"><MapPin size={17} /></span>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Location</p>
                    <p className="text-sm font-semibold" data-testid="contact-location-text">{COMPANY.location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3.5">
                  <span className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0"><Clock size={17} /></span>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Working Hours</p>
                    <p className="text-sm font-semibold">Mon – Sat · 9:30 AM – 7:00 PM IST</p>
                  </div>
                </div>
                <div className="flex gap-2.5 pt-2">
                  {socials.map(({ icon: Icon, label, id }) => (
                    <a key={id} href="#" aria-label={label} data-testid={`contact-social-${id}`}
                      className="w-9 h-9 rounded-full border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors duration-200">
                      <Icon size={15} />
                    </a>
                  ))}
                </div>
              </div>
              <div className="glass-card rounded-2xl overflow-hidden" data-testid="contact-map-card">
                <iframe title="VAELIX TECHNOLOGY office location — Pune, Maharashtra, India"
                  src="https://www.google.com/maps?q=Pune,+Maharashtra,+India&output=embed"
                  className="w-full h-64 border-0" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <div className="relative rounded-2xl overflow-hidden border border-border/60 shadow-lg group" data-testid="contact-visit-card">
                <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80"
                  alt="VAELIX TECHNOLOGY team collaborating"
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-indigo-950/85 via-indigo-950/20 to-transparent flex items-end p-5">
                  <div>
                    <p className="text-white font-display font-semibold">Visit our Pune office</p>
                    <p className="text-slate-300 text-xs mt-1">Response within 24 hours, guaranteed.</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
